import { ProductTransportType } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { Separator } from '@shared/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@shared/components/ui/sheet';
import { cn, formatNumber } from '@shared/lib/utils';
import ProductMetric from '../components/product-metric';

interface ProductDetailTransportSheetProps
  extends React.HTMLAttributes<HTMLDivElement> {
  product: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  transportType: ProductTransportType;
  totalEmissions: number;
}

export default function ProductDetailTransportSheet({
  className,
  isOpen,
  setIsOpen,
  transportType,
  product,
  totalEmissions,
  ...props
}: ProductDetailTransportSheetProps) {
  const stepEmissions = parseFloat(transportType.totalEmissionsPerItem);
  const shareOfTotalEmissions =
    (parseFloat(transportType.totalEmissionsPerItem) / totalEmissions) * 100;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className={cn('flex h-full flex-col sm:max-w-2xl', className)}
        {...props}
      >
        <SheetHeader className="space-y-2 border-b pb-4">
          <Text>{product}</Text>
          <SheetTitle className="text-xl font-semibold">
            {transportType.name}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-y-auto">
          <div className="flex">
            <ProductMetric
              title="Step emissions"
              className="flex-1 space-y-0"
              value={formatNumber(stepEmissions)}
              unit="kgCO2e"
              valueClassName="text-2xl"
            />
            <ProductMetric
              title="Share of total emissions"
              className="flex-1 space-y-0"
              value={formatNumber(shareOfTotalEmissions)}
              unit="%"
              valueClassName="text-2xl"
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="space-y-2">
              <Text variant="subSection">Transport modelling</Text>
              <Text className="font-light text-muted-foreground">
                This item is linked to a transport journey in your account.
              </Text>
            </div>

            <div>
              <Text className="font-semibold">Linked to</Text>
              <div className="flex items-center gap-1.5 ">
                <Icons.truck size={16} />
                <Text className="">{transportType.name}</Text>
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_6rem_4rem_5rem] items-start border-b pb-1.5">
                <Text className="text-xs font-semibold">Journey</Text>
                <Text className="text-right text-xs font-semibold">Mode</Text>
                <div className="text-right">
                  <Text className="text-xs font-semibold">Distance</Text>
                  <Unit
                    variant="km"
                    className="block text-[10px] text-muted-foreground"
                  />
                </div>
                <div className="text-right">
                  <Text className="text-xs font-semibold">Emissions</Text>
                  <Unit
                    variant="kgCO2e"
                    className="block text-[10px] text-muted-foreground"
                  />
                </div>
              </div>
              {transportType.journeys.map((journey) => {
                const modes = journey.modes as string[];
                const modeLabel = modes.join(', ') || '—';
                return (
                  <div
                    key={journey.name}
                    className="grid grid-cols-[1fr_6rem_4rem_5rem] items-center"
                  >
                    <Text className="line-clamp-2 truncate text-sm text-muted-foreground">
                      {journey.name}
                    </Text>
                    <Text className="text-right text-sm text-muted-foreground">
                      {modeLabel}
                    </Text>
                    <Text className="text-right text-sm text-muted-foreground">
                      {formatNumber(journey.totalDistance)}
                    </Text>
                    <Text className="text-right text-sm text-muted-foreground">
                      {formatNumber(parseFloat(journey.totalEmissions))}
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="shrink-0 pt-3">
          <Text className="text-xs text-muted-foreground">
            This information is based on a snapshot of the data at the time of
            the last assessment.
          </Text>
        </div>
      </SheetContent>
    </Sheet>
  );
}
